import { Clipboard } from '@angular/cdk/clipboard';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  type Signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';

/**
 * A settings card heading that can be linked to directly.
 *
 * The anchor is an explicit input rather than a slug derived from the heading
 * text. These headings are copy that has already been reworded more than once,
 * and deriving the anchor from the wording would silently break every saved
 * link the next time someone edits a heading.
 *
 * The copy button is deliberately a sibling of the `<h2>` rather than a child.
 * A `<mat-icon>` inside the heading would put its ligature text into
 * `h2.textContent`, which the section-layout tests assert on exactly.
 */
@Component({
  selector: 'pr-deposit-section-heading',
  templateUrl: './deposit-section-heading.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
})
export class DepositSectionHeadingComponent {
  private _clipboard = inject(Clipboard);
  private _snackBar = inject(MatSnackBar);
  private _document = inject(DOCUMENT);

  readonly heading = input.required<string>();
  readonly anchor = input.required<string>();
  readonly copyLabel: Signal<string> = computed(
    () => `Copy link to ${this.heading()}`
  );

  /**
   * Copies an absolute link to this section, so it can be pasted into a doc or
   * a message. Any fragment already on the URL is stripped first, otherwise
   * copying a second heading would append its anchor to the first one's.
   */
  copyLink(): void {
    const pageUrl = this._document.location.href.replace(/#.*$/, '');
    this._clipboard.copy(`${pageUrl}#${this.anchor()}`);
    this._snackBar.open('Copied to clipboard');
  }
}
